import { Slot } from "@radix-ui/react-slot";
import { cn } from "@workspace/ui/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentProps } from "react";

const badgeVariants = cva(
  "inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border px-2 py-0.5 font-medium text-xs transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "border-transparent bg-destructive text-white focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40 [a&]:hover:bg-destructive/90",
        outline:
          "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        tag: "rounded-none border-none bg-muted font-mono font-normal text-[10px] text-muted-foreground",
        "tag-rounded":
          "border-none bg-muted px-3 py-1 font-mono font-normal text-[12px] text-muted-foreground",
        "tag-outline":
          "border-transparent bg-secondary-foreground text-secondary hover:bg-secondary-foreground/80",
        red: "border-red-500/20 bg-red-500/10 text-red-700 dark:text-red-400",
        orange:
          "border-orange-500/20 bg-orange-500/10 text-orange-700 dark:text-orange-400",
        amber:
          "border-amber-500/20 bg-amber-500/10 text-amber-700 dark:text-amber-400",
        yellow:
          "border-yellow-500/20 bg-yellow-500/10 text-yellow-700 dark:text-yellow-400",
        lime: "border-lime-500/20 bg-lime-500/10 text-lime-700 dark:text-lime-400",
        green:
          "border-green-500/20 bg-green-500/10 text-green-700 dark:text-green-400",
        emerald:
          "border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
        teal: "border-teal-500/20 bg-teal-500/10 text-teal-700 dark:text-teal-400",
        cyan: "border-cyan-500/20 bg-cyan-500/10 text-cyan-700 dark:text-cyan-400",
        sky: "border-sky-500/20 bg-sky-500/10 text-sky-700 dark:text-sky-400",
        blue: "border-blue-500/20 bg-blue-500/10 text-blue-700 dark:text-blue-400",
        indigo:
          "border-indigo-500/20 bg-indigo-500/10 text-indigo-700 dark:text-indigo-400",
        violet:
          "border-violet-500/20 bg-violet-500/10 text-violet-700 dark:text-violet-400",
        purple:
          "border-purple-500/20 bg-purple-500/10 text-purple-700 dark:text-purple-400",
        fuchsia:
          "border-fuchsia-500/20 bg-fuchsia-500/10 text-fuchsia-700 dark:text-fuchsia-400",
        pink: "border-pink-500/20 bg-pink-500/10 text-pink-700 dark:text-pink-400",
        rose: "border-rose-500/20 bg-rose-500/10 text-rose-700 dark:text-rose-400",
        neutral:
          "border-neutral-500/20 bg-neutral-500/10 text-neutral-700 dark:text-neutral-300",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : "span";

  return (
    <Comp
      className={cn(badgeVariants({ variant }), className)}
      data-slot="badge"
      {...props}
    />
  );
}

// react-doctor-disable-next-line react-doctor/only-export-components -- shadcn/ui registry file — variant exports alongside components is the documented pattern
export { Badge, badgeVariants };
